import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { broadcastApi } from '../api';
import toast from 'react-hot-toast';
import {
    FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight,
    FiCode, FiLink, FiRefreshCw, FiGlobe, FiInfo
} from 'react-icons/fi';
import { inputClass, btnPrimary, btnGhost, iconBtn, th, statusPill } from '../components/Broadcast/ui';

const emptyForm = {
    name: '',
    allowed_domains: '',
    logged_in_selector: '',
    logged_in_cookie: '',
    notes: '',
    is_active: true
};

function copy(text, label) {
    const done = () => toast.success(`${label} copied`);
    try {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(done).catch(() => toast.error('Copy failed'));
            return;
        }
    } catch (e) { /* fall through to the legacy path */ }
    try {
        const area = document.createElement('textarea');
        area.value = text;
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
        done();
    } catch (e) {
        toast.error('Copy failed');
    }
}

export default function BroadcastPanelsPage() {
    const [panels, setPanels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [snippetFor, setSnippetFor] = useState(null);

    const fetchPanels = async () => {
        try {
            const { data } = await broadcastApi.listPanels();
            setPanels(data.embeds || []);
        } catch (err) {
            toast.error('Failed to load broadcast panels');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPanels(); }, []);

    const openAdd = () => {
        setEditing(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (panel) => {
        setEditing(panel);
        setForm({
            name: panel.name || '',
            allowed_domains: (panel.allowed_domains || []).join(', '),
            logged_in_selector: panel.logged_in_selector || '',
            logged_in_cookie: panel.logged_in_cookie || '',
            notes: panel.notes || '',
            is_active: !!panel.is_active
        });
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                allowed_domains: form.allowed_domains.split(/[\s,]+/).filter(Boolean)
            };
            const { data } = editing
                ? await broadcastApi.updatePanel(editing.id, payload)
                : await broadcastApi.createPanel(payload);

            toast.success(editing ? 'Panel updated' : 'Panel added');
            setShowModal(false);
            await fetchPanels();
            if (!editing && data.embed) setSnippetFor(data.embed);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save panel');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (panel) => {
        try {
            await broadcastApi.updatePanel(panel.id, { ...panel, allowed_domains: panel.allowed_domains, is_active: !panel.is_active });
            toast.success(panel.is_active ? 'Panel disabled' : 'Panel enabled');
            fetchPanels();
        } catch (err) {
            toast.error('Failed to update panel');
        }
    };

    const handleRotate = async (panel) => {
        if (!confirm(`Rotate the embed key for "${panel.name}"?\n\nThe current footer snippet stops working immediately and the panel owner must paste the new one.`)) return;
        try {
            const { data } = await broadcastApi.rotatePanelKey(panel.id);
            toast.success('New key generated');
            await fetchPanels();
            if (data.embed) setSnippetFor(data.embed);
        } catch (err) {
            toast.error('Failed to rotate key');
        }
    };

    const handleDelete = async (panel) => {
        if (!confirm(`Delete "${panel.name}"?\n\nEvery broadcast sent to this panel, and its statistics, are deleted with it. This cannot be undone.`)) return;
        try {
            await broadcastApi.deletePanel(panel.id);
            toast.success('Panel deleted');
            fetchPanels();
        } catch (err) {
            toast.error('Failed to delete panel');
        }
    };

    return (
        <DashboardLayout>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Broadcast Panels</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Panels that show your popups. Add one, then paste its snippet into that panel's footer.
                    </p>
                </div>
                <button onClick={openAdd} className={`${btnPrimary} w-full sm:w-auto`}>
                    <FiPlus size={16} /> Add Panel
                </button>
            </div>

            <div className="mb-6 flex gap-3 rounded-lg border border-border bg-muted/20 p-4">
                <FiInfo size={18} className="mt-0.5 flex-shrink-0 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                    The panel only needs the snippet — no theme edits, no login, nothing installed on their side.
                    Change the audience, the pages or the wording later from here and the panel picks it up on its
                    next page load. The snippet is public, so keep <strong className="text-foreground">Allowed sites</strong> filled in
                    unless you want the popups readable from anywhere.
                </p>
            </div>

            <div className="card overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto min-h-[200px]">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-border bg-muted/20">
                                    <th className={th}>Panel</th>
                                    <th className={th}>Allowed sites</th>
                                    <th className={th}>Signed-in detection</th>
                                    <th className={th}>Status</th>
                                    <th className={th}>Added</th>
                                    <th className={`${th} text-right`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                                {panels.map(panel => (
                                    <tr key={panel.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                                        <td className="p-4 align-middle">
                                            <div className="font-medium text-foreground">{panel.name}</div>
                                            {panel.notes && <div className="text-xs text-muted-foreground mt-0.5">{panel.notes}</div>}
                                        </td>
                                        <td className="p-4 align-middle">
                                            {panel.allowed_domains?.length ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {panel.allowed_domains.map(domain => (
                                                        <span key={domain} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                                            <FiGlobe size={11} /> {domain}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-amber-600 dark:text-amber-400">Any site — not restricted</span>
                                            )}
                                        </td>
                                        <td className="p-4 align-middle text-xs text-muted-foreground">
                                            {panel.logged_in_selector
                                                ? <code className="text-xs">{panel.logged_in_selector}</code>
                                                : panel.logged_in_cookie
                                                    ? <code className="text-xs">cookie: {panel.logged_in_cookie}</code>
                                                    : <span>Auto (panel user data)</span>}
                                        </td>
                                        <td className="p-4 align-middle">
                                            <span className={statusPill(panel.is_active ? 'live' : 'off')}>
                                                {panel.is_active ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </td>
                                        <td className="p-4 align-middle text-xs text-muted-foreground">
                                            {panel.created_at ? String(panel.created_at).slice(0, 10) : '—'}
                                        </td>
                                        <td className="p-4 align-middle">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => setSnippetFor(panel)} className={iconBtn} title="Show footer snippet">
                                                    <FiCode size={15} />
                                                </button>
                                                <button
                                                    onClick={() => copy(panel.owner_url, 'Owner report link')}
                                                    className={iconBtn}
                                                    title="Copy the read-only report link for the panel owner"
                                                >
                                                    <FiLink size={15} />
                                                </button>
                                                <button onClick={() => handleRotate(panel)} className={iconBtn} title="Rotate embed key">
                                                    <FiRefreshCw size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleToggle(panel)}
                                                    className={iconBtn}
                                                    title={panel.is_active ? 'Disable broadcasts' : 'Enable broadcasts'}
                                                >
                                                    {panel.is_active ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                                                </button>
                                                <button onClick={() => openEdit(panel)} className={iconBtn} title="Edit">
                                                    <FiEdit2 size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(panel)}
                                                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                                    title="Delete"
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {panels.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                                            No panels yet. Click "Add Panel" to create one and get its footer snippet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                    <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-border">
                            <h3 className="text-lg font-semibold tracking-tight text-foreground">
                                {editing ? 'Edit Broadcast Panel' : 'Add Broadcast Panel'}
                            </h3>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Panel name *</label>
                                <input type="text" required value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    className={inputClass} placeholder="e.g. Friend's panel" />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Allowed sites</label>
                                <input type="text" value={form.allowed_domains}
                                    onChange={e => setForm({ ...form, allowed_domains: e.target.value })}
                                    className={inputClass} placeholder="panel.com, www.panel.com" />
                                <p className="text-xs text-muted-foreground">
                                    Comma separated. Subdomains are included. Leave empty to allow any site — the popups
                                    are then readable by anyone who copies the snippet.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Signed-in detection</label>
                                <input type="text" value={form.logged_in_selector}
                                    onChange={e => setForm({ ...form, logged_in_selector: e.target.value })}
                                    className={inputClass} placeholder="CSS selector, e.g. .user-menu" />
                                <input type="text" value={form.logged_in_cookie}
                                    onChange={e => setForm({ ...form, logged_in_cookie: e.target.value })}
                                    className={inputClass} placeholder="…or a cookie name" />
                                <p className="text-xs text-muted-foreground">
                                    Only needed if this panel does not expose the user's data to the page. If it does,
                                    leave both empty and detection is automatic. Fill one in only if guests are wrongly
                                    counted as signed in, or vice versa.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Note</label>
                                <input type="text" value={form.notes}
                                    onChange={e => setForm({ ...form, notes: e.target.value })}
                                    className={inputClass} placeholder="Who owns this panel, contact, etc." />
                            </div>

                            <label className="flex items-center gap-2 text-sm text-foreground">
                                <input type="checkbox" checked={form.is_active}
                                    onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                                Enabled — serve popups to this panel
                            </label>

                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowModal(false)} className={btnGhost}>Cancel</button>
                                <button type="submit" disabled={saving} className={btnPrimary}>
                                    {saving ? 'Saving…' : editing ? 'Update' : 'Add Panel'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {snippetFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                    <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-border">
                            <h3 className="text-lg font-semibold tracking-tight text-foreground">
                                Paste this into {snippetFor.name}
                            </h3>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground">
                                    In that panel's admin, open <strong className="text-foreground">Settings → Custom footer code</strong> and paste
                                    this line at the end. Nothing else is needed — no theme change, no file upload.
                                </p>
                                <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed text-foreground whitespace-pre-wrap break-all">
{snippetFor.snippet}
                                </pre>
                                <button
                                    type="button"
                                    onClick={() => copy(snippetFor.snippet, 'Snippet')}
                                    className={btnPrimary}
                                >
                                    <FiCode size={15} /> Copy snippet
                                </button>
                            </div>

                            <div className="space-y-2 border-t border-border pt-5">
                                <label className="text-sm font-medium leading-none text-foreground">Owner report link</label>
                                <p className="text-sm text-muted-foreground">
                                    Send this to whoever owns the panel. It opens a read-only report of what was sent
                                    and how it performed — no account, no login. Treat it as a password: anyone with
                                    the link can read the panel's stats.
                                </p>
                                <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 text-xs text-foreground whitespace-pre-wrap break-all">
{snippetFor.owner_url}
                                </pre>
                                <button
                                    type="button"
                                    onClick={() => copy(snippetFor.owner_url, 'Owner report link')}
                                    className={btnGhost}
                                >
                                    <FiLink size={15} /> Copy link
                                </button>
                            </div>

                            <div className="flex justify-end pt-1">
                                <button type="button" onClick={() => setSnippetFor(null)} className={btnGhost}>Done</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
